import React from 'react';
import { Globe, Users, Handshake, GraduationCap, Building, Lightbulb, ArrowRight } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../context/LanguageContext';

const ProjectsPage = () => {
  const { t } = useLanguage();

  const partnerIcons = [Building, GraduationCap, Building, Lightbulb, GraduationCap];

  return (
    <div className="min-h-screen bg-[#060b1a]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#060b1a] via-[#0a1628] to-[#0f1f33]" />
        <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-[#4DD6FF]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 left-1/3 w-80 h-80 bg-[#FF4DA6]/10 rounded-full blur-[120px]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full border border-[#4DD6FF]/30 bg-[#4DD6FF]/5 text-[#4DD6FF] text-sm font-medium mb-6">
              {t('projects.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
              {t('projects.title')}
            </h1>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="relative py-16 bg-[#060b1a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm mb-16">
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('projects.intro')}
            </p>
          </div>

          {/* KA1 Section */}
          <div className="mb-12">
            <div className="p-8 rounded-2xl border border-[#4DFF88]/20 bg-gradient-to-br from-[#4DFF88]/5 to-transparent">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#4DFF88]/10 flex items-center justify-center">
                  <Users className="w-7 h-7 text-[#4DFF88]" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-white">{t('projects.ka1.title')}</h2>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                {t('projects.ka1.description')}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(() => {
                  const features = t('projects.ka1.features');
                  return Array.isArray(features) && features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-300">
                      <ArrowRight className="w-4 h-4 text-[#4DFF88]" />
                      <span>{feature}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* KA2 Section */}
          <div className="mb-12">
            <div className="p-8 rounded-2xl border border-[#4DD6FF]/20 bg-gradient-to-br from-[#4DD6FF]/5 to-transparent">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#4DD6FF]/10 flex items-center justify-center">
                  <Handshake className="w-7 h-7 text-[#4DD6FF]" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-white">{t('projects.ka2.title')}</h2>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                {t('projects.ka2.description')}
              </p>
              
              <h3 className="text-lg font-semibold text-white mb-4">
                {t('header.home') === 'Home' ? 'Our Partners' : 'İş Birliği Ortaklarımız'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                {(() => {
                  const partners = t('projects.ka2.partners');
                  return Array.isArray(partners) && partners.map((partner, index) => {
                    const IconComponent = partnerIcons[index];
                    return (
                      <div key={index} className="text-center p-4 rounded-xl bg-white/5">
                        <div className="w-10 h-10 rounded-lg bg-[#4DD6FF]/10 flex items-center justify-center mx-auto mb-2">
                          <IconComponent className="w-5 h-5 text-[#4DD6FF]" />
                        </div>
                        <p className="text-sm text-gray-300">{partner}</p>
                      </div>
                    );
                  });
                })()}
              </div>

              <h3 className="text-lg font-semibold text-white mb-4">
                {t('header.home') === 'Home' ? 'Project Outcomes' : 'Proje Çıktıları'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(() => {
                  const outcomes = t('projects.ka2.outcomes');
                  return Array.isArray(outcomes) && outcomes.map((outcome, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-300">
                      <ArrowRight className="w-4 h-4 text-[#4DD6FF]" />
                      <span>{outcome}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="p-8 rounded-2xl border border-white/10 bg-white/5 text-center">
            <Globe className="w-12 h-12 text-[#4DFF88] mx-auto mb-4" />
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('projects.vision')}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
